import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Whatshot, 
  TrendingUp, 
  Movie, 
  Tv, 
  Add, 
  Remove,
  Refresh,
  Analytics
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { canManageHypeRating } from '../../utils/adminUtils';
import { adminHypeRatingUtils } from '../../utils/adminHypeRatingUtils';
import HypeRating from '../Movies/HypeRating';

const client = generateClient();

const HypeRatingManager = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [boostDialog, setBoostDialog] = useState(false);
  const [boostAmount, setBoostAmount] = useState(0);
  const [boostReason, setBoostReason] = useState('');
  const [contentType, setContentType] = useState('MOVIE');
  const [bulkRecalculating, setBulkRecalculating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);

  const searchContent = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const results = await adminHypeRatingUtils.searchContent(searchQuery, 20);
      // Filter out results without basic required fields
      const validResults = results.filter(item => item.id && item.name);
      setSearchResults(validResults);
      
      if (validResults.length === 0) {
        addNotification({
          type: 'info',
          message: 'No content found matching your search'
        });
      }
    } catch (error) {
      console.error('Error searching content:', error);
      addNotification({
        type: 'error',
        message: 'Failed to search content. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const openBoostDialog = (content) => {
    setSelectedContent(content);
    setBoostDialog(true);
    setBoostAmount(0);
    setBoostReason('');
  };

  const applyHypeBoost = async () => {
    if (!selectedContent || !boostReason.trim()) {
      addNotification({
        type: 'error',
        message: 'Please provide a reason for the boost'
      });
      return;
    }

    try {
      const result = await adminHypeRatingUtils.boostHypeRating(
        selectedContent.id,
        selectedContent.type,
        boostAmount,
        boostReason,
        user.id
      );

      // Update local state with the new rating from backend (don't add boostAmount again)
      setSearchResults(prev => 
        prev.map(item => 
          item.id === selectedContent.id 
            ? { ...item, hypeRating: result.newHypeRating }
            : item
        )
      );

      addNotification({
        type: 'success',
        message: `HypeRating boosted by ${boostAmount} points`
      });

      setBoostDialog(false);
    } catch (error) {
      console.error('Error applying hype boost:', error);
      addNotification({
        type: 'error',
        message: 'Failed to apply hype boost'
      });
    }
  };

  const recalculateHypeRating = async (content) => {
    try {
      const result = await adminHypeRatingUtils.recalculateHypeRating(content.id, content.type);
      
      // Update local state with new rating
      setSearchResults(prev => 
        prev.map(item => 
          item.id === content.id 
            ? { ...item, hypeRating: result.hypeRating, hypeScore: result.hypeScore }
            : item
        )
      );

      addNotification({
        type: 'success',
        message: 'HypeRating recalculated successfully'
      });
    } catch (error) {
      console.error('Error recalculating hype rating:', error);
      addNotification({
        type: 'error',
        message: 'Failed to recalculate HypeRating'
      });
    }
  };

  const bulkRecalculateHypeRating = async () => {
    setBulkRecalculating(true);
    setBulkProgress(null);
    
    try {
      const result = await adminHypeRatingUtils.bulkRecalculateHypeRating();
      setBulkProgress(result);
      
      addNotification({
        type: 'success',
        message: `Bulk recalculation completed: ${result.successful}/${result.totalContent} successful`
      });
    } catch (error) {
      console.error('Error in bulk recalculation:', error);
      addNotification({
        type: 'error',
        message: 'Bulk recalculation failed'
      });
    } finally {
      setBulkRecalculating(false);
    }
  };

  const getHypeLevel = (rating) => {
    if (rating >= 8) return { label: 'Viral', color: '#ff4444' };
    if (rating >= 6) return { label: 'Hot', color: '#ff8800' };
    if (rating >= 4) return { label: 'Trending', color: '#ffaa00' };
    if (rating >= 2) return { label: 'Rising', color: '#44aa44' };
    return { label: 'New', color: '#888888' };
  };

  if (!canManageHypeRating(user)) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        HypeRating Manager
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage and boost HypeRatings for movies and shows
      </Typography>

      {/* Bulk Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bulk Actions
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              color="warning"
              onClick={bulkRecalculateHypeRating}
              disabled={bulkRecalculating}
              startIcon={bulkRecalculating ? <CircularProgress size={20} /> : <Refresh />}
            >
              {bulkRecalculating ? 'Recalculating...' : 'Recalculate All HypeRatings'}
            </Button>
            {bulkProgress && (
              <Typography variant="body2" color="text.secondary">
                Processed: {bulkProgress.processed}/{bulkProgress.totalContent} 
                (Success: {bulkProgress.successful}, Failed: {bulkProgress.failed})
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              label="Search Movies & Shows"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchContent()}
              placeholder="Enter movie or show name..."
            />
            <Button
              variant="contained"
              onClick={searchContent}
              disabled={loading || !searchQuery.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <Analytics />}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Grid container spacing={3}>
          {searchResults.map((content) => {
            const hypeLevel = getHypeLevel(content.hypeRating || 0);
            
            return (
              <Grid item xs={12} md={6} lg={4} key={`${content.type}-${content.id}`}>
                <Card>
                  <CardContent>
                    <Box display="flex" gap={2} mb={2}>
                      {content.poster && (
                        <img
                          src={content.poster}
                          alt={content.name}
                          style={{
                            width: 60,
                            height: 90,
                            objectFit: 'cover',
                            borderRadius: 8,
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            if (content.type === 'MOVIE') {
                              window.open(`/movie/${content.id}`, '_blank');
                            } else {
                              window.open(`/show/${content.id}`, '_blank');
                            }
                          }}
                        />
                      )}
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {content.name}
                        </Typography>
                        <Box display="flex" gap={1} mb={1}>
                          <Chip
                            icon={content.type === 'MOVIE' ? <Movie /> : <Tv />}
                            label={content.type}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={hypeLevel.label}
                            size="small"
                            sx={{ 
                              backgroundColor: hypeLevel.color,
                              color: 'white'
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Language: {content.language || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>

                    <HypeRating content={content} />

                    <Box display="flex" gap={1} mt={2}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<TrendingUp />}
                        onClick={() => openBoostDialog(content)}
                      >
                        Boost
                      </Button>
                      <Tooltip title="Recalculate HypeRating">
                        <IconButton
                          size="small"
                          onClick={() => recalculateHypeRating(content)}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Boost Dialog */}
      <Dialog open={boostDialog} onClose={() => setBoostDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Boost HypeRating
        </DialogTitle>
        <DialogContent>
          {selectedContent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedContent.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current HypeRating: {selectedContent.hypeRating?.toFixed(1) || '0.0'}/10
              </Typography>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography gutterBottom>
                  Boost Amount: {boostAmount} points
                </Typography>
                <Slider
                  value={boostAmount}
                  onChange={(e, value) => setBoostAmount(value)}
                  min={-5}
                  max={5}
                  step={0.1}
                  marks={[
                    { value: -5, label: '-5' },
                    { value: 0, label: '0' },
                    { value: 5, label: '+5' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                New HypeRating: {Math.min(10, Math.max(0, (selectedContent.hypeRating || 0) + boostAmount)).toFixed(1)}/10
              </Typography>

              <TextField
                fullWidth
                label="Reason for Boost"
                value={boostReason}
                onChange={(e) => setBoostReason(e.target.value)}
                multiline
                rows={3}
                placeholder="Explain why this content deserves a HypeRating boost..."
                sx={{ mt: 2 }}
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoostDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={applyHypeBoost}
            variant="contained"
            disabled={!boostReason.trim()}
          >
            Apply Boost
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HypeRatingManager;